/**
 * useFindReplace — Replace all: send the table and the pairs, get the
 * corrected rows back.
 *
 *   const { replaceAll, replacing } = useFindReplace({ mode, provider, model });
 *   const nextRows = await replaceAll(rows, pairs);   // null when it failed
 *
 * The server does the replacing and re-checks every row (POST /input/correct,
 * api/instancesApi.js). On success the rows come back unsaved — Save still has
 * to be clicked, as legacy. On failure the toast says why and null is
 * returned, so the page keeps its rows — legacy wiped the table instead.
 *
 * By product (`mode`, default: the running product): the request is the same;
 * only how the corrected rows are folded back differs (CCA keeps quantity /
 * hours as strings and the server's `adjusted` — components/cca/ccaFindReplace).
 */
import { mergeCorrectedRows as mergeCcaCorrectedRows } from '@components/cca/ccaFindReplace';
import {
  mergeCorrectedRows as mergeEiaCorrectedRows,
  toCorrectPairs,
} from '@components/eia/findReplaceRules';
import { toApiRow } from '@components/eia/instanceRows';
import { useNotify } from '@hooks/useNotify';
import { APP_MODES, resolveAppMode } from '@variant/appMode';

import {
  CORRECT_FAILED_MESSAGE,
  readCorrectedRows,
  useCorrectInstancesMutation,
} from '../api/instancesApi';

export function useFindReplace({ mode = resolveAppMode(), provider, model }) {
  const mergeCorrectedRows = mode === APP_MODES.CCA ? mergeCcaCorrectedRows : mergeEiaCorrectedRows;
  const [correct, { isLoading }] = useCorrectInstancesMutation();
  const notify = useNotify();

  const replaceAll = async (rows, pairs) => {
    try {
      const corrected = readCorrectedRows(
        await correct({
          provider,
          model,
          pairs: toCorrectPairs(pairs),
          rows: rows.map(toApiRow),
        }).unwrap(),
      );
      // Legacy is silent here; every other table change in this app says so.
      notify.success('Values replaced. Review the instances and click Save.');
      return mergeCorrectedRows(rows, corrected);
    } catch (error) {
      notify.error(error, CORRECT_FAILED_MESSAGE);
      return null;
    }
  };

  return { replaceAll, replacing: isLoading };
}
